'use client';

import { ContentType } from '@jlg-website/shared/constants';
import React, { useEffect, useState } from 'react';
import type { ContentType as TContentType, Icon, IconWithPack } from '@jlg-website/database/types';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Pencil } from 'lucide-react';
import dynamic from 'next/dynamic';
import type { ContentTypeAccidentTypeContentInput } from '@jlg-website/shared';

const AccidentTypesForm = dynamic(
  () =>
    import('@/components/admin/contents/content-types/accident-types-form').then(
      (mod) => mod.AccidentTypesForm
    ),
  { loading: () => <>Loading...</> }
);

const fetchContentType = async () => {
  const request = await fetch(`/admin/api/content-type?name=${ContentType.AccidentTypes}`, {
    cache: 'no-store',
  });
  return request.json();
};

const fetchContent = async (contentId: string) => {
  const request = await fetch(`/admin/api/contents/${contentId}`, { cache: 'no-store' });
  return request.json();
};

const fetchIcons = async () => {
  const request = await fetch(`/admin/api/icons`, {
    cache: 'no-store',
  });
  return request.json();
};

export function AccidentTypesEditDialogForm({
  onEdit,
  value,
}: {
  onEdit: () => void;
  value: string;
}) {
  const [open, setOpen] = useState(false);
  const [contentType, setContentType] = useState<TContentType | null>(null);
  const [icons, setIcons] = useState<IconWithPack[] | null>(null);
  const [defaultValues, setDefaultValues] = useState<ContentTypeAccidentTypeContentInput | null>(
    null
  );

  const handleSave = () => {
    setOpen(false);
    onEdit();
  };

  useEffect(() => {
    if (!open) return;

    (async () => {
      const [contentType, content, icons] = await Promise.all([
        fetchContentType(),
        fetchContent(value),
        fetchIcons(),
      ]);

      setContentType(contentType);
      setDefaultValues(content);
      setIcons(icons);
    })();
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) {
          setContentType(null);
          setDefaultValues(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full">
          <Pencil /> Edit selected
        </Button>
      </DialogTrigger>
      <DialogContent className="lg:min-w-3xl gap-0 p-0">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-0">
          <DialogTitle>Edit content</DialogTitle>
        </DialogHeader>
        {!contentType || !defaultValues ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 pt-4 pb-6">
            <AccidentTypesForm
              contentType={contentType}
              defaultValues={defaultValues}
              icons={icons as Icon[]}
              onSave={handleSave}
              customCancelButton={
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
              }
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AccidentTypesEditDialogForm;
